// /modules/guides/:guideKey — buyer-oriented collections over the canonical capability catalogue.
// A guide explains a school job across several reviewed module pages; it is not a new entitlement,
// a second module inventory, or evidence that every neighbouring hand-off is automatic.
import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CircleUserRound,
  Layers3,
  Route,
  ShieldCheck,
} from "lucide-react";
import { BrandCopy } from "../components/BrandWordmarkText";
import { MarketingTopBar, SiteFooter, ContactSalesModal } from "../components/marketing/shared";
import { MarketingMotion } from "../components/marketing/MarketingMotion";
import { ScrollProgressBar } from "../components/marketing/scrollMotion";
import MarketingSeo, { seoOrigin } from "../components/marketing/MarketingSeo";
import { Button } from "../components/ui/primitives";
import { CTA_PRIMARY_LABEL, CTA_SECONDARY_LABEL } from "../components/marketing/navigation";
import { init as initAnalytics, track } from "../lib/analytics";
import { moduleByKey } from "./landingparts/moduleCatalog";
import { moduleBuyerGuideFor } from "./landingparts/moduleBuyerGuides";
import BuyerGuideVisualStage from "./landingparts/BuyerGuideVisualStage";
import { buyerGuideFaqsFor, faqPageSchemaFor } from "./landingparts/marketingFaqSchema";

const copyOf = (value) => {
  if (typeof value === "string") return value;
  if (!value || typeof value !== "object") return "";
  return value.description || value.detail || value.summary || value.copy || value.body || "";
};

const titleOf = (value, fallback = "") => {
  if (typeof value === "string") return value;
  if (!value || typeof value !== "object") return fallback;
  return value.title || value.label || value.name || fallback;
};

function guideKind(guide) {
  return String(guide.type || "").includes("capability") ? "Capability guide" : "Buyer guide";
}

function guideStatus(guide) {
  if (typeof guide.status === "string") return guide.status;
  return titleOf(guide.status, "Reviewed product scope");
}

function validModules(keys = []) {
  const list = Array.isArray(keys) ? keys : [keys];
  return [...new Set(list)].map((key) => moduleByKey[key]).filter(Boolean);
}

function ScopeList({ items, tone = "included" }) {
  const isIncluded = tone === "included";
  return (
    <ul className="mt-6 border-t border-line" data-testid={`buyer-guide-${tone}`}>
      {(items || []).map((item, index) => {
        const title = titleOf(item);
        const detail = typeof item === "string" ? "" : copyOf(item);
        return (
          <li key={`${title}-${index}`} className="grid grid-cols-[28px_minmax(0,1fr)] gap-3 border-b border-line py-4">
            <span
              className={`mt-0.5 grid h-6 w-6 place-items-center rounded-full ${isIncluded ? "bg-positive-soft text-positive" : "bg-muted text-ink-2"}`}
              aria-hidden="true"
            >
              {isIncluded ? <Check className="h-3.5 w-3.5" /> : <span className="text-[12px] font-bold">{String(index + 1).padStart(2, "0")}</span>}
            </span>
            <span>
              <strong className="block text-[13.5px] font-semibold leading-relaxed text-ink"><BrandCopy>{title}</BrandCopy></strong>
              {detail && detail !== title ? <span className="mt-1 block text-[12.5px] leading-relaxed text-ink-2"><BrandCopy>{detail}</BrandCopy></span> : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function BuyerPassage({ guide }) {
  const primaryKeys = guide.primaryCapabilityKeys || [];
  const stages = (guide.workflow || []).map((stage, index) => {
    const explicitKeys = typeof stage === "object"
      ? stage.capabilityKeys || [stage.capabilityKey || stage.moduleKey || stage.capability].filter(Boolean)
      : [];
    const fallbackKey = primaryKeys[index % Math.max(primaryKeys.length, 1)];
    return {
      title: titleOf(stage, `Stage ${index + 1}`),
      description: typeof stage === "string" ? "" : copyOf(stage),
      capabilities: validModules(explicitKeys.length ? explicitKeys : [fallbackKey]),
    };
  });

  return (
    <section className="border-y border-line bg-surface" aria-labelledby="buyer-passage-heading" data-testid="buyer-guide-workflow">
      <div className="mx-auto max-w-7xl px-5 py-14 sm:py-16">
        <div className="grid gap-6 lg:grid-cols-[minmax(230px,0.55fr)_minmax(0,1.45fr)] lg:gap-14">
          <div>
            <p className="text-[12px] font-bold uppercase tracking-[0.13em] text-accent">The work, stage by stage</p>
            <h2 id="buyer-passage-heading" className="mt-2 font-heading text-[28px] font-bold leading-tight tracking-tight text-ink sm:text-[34px]">
              Follow the work, not a menu.
            </h2>
            <p className="mt-4 max-w-md text-[14px] leading-[1.75] text-ink-2">
              Where a reviewed catalogue route directly explains a stage, it is linked below. Some shipped capability stages do not have a separate public catalogue page.
            </p>
          </div>

          <ol className="overflow-hidden rounded-[24px] border border-line bg-canvas md:grid md:grid-cols-2" data-testid="buyer-passage-stages">
            {stages.map((stage, index) => {
              const content = (
                <>
                  <span className="flex items-center justify-between gap-3">
                    <span className="text-[12px] font-bold uppercase tracking-[0.12em] text-accent">Stage {String(index + 1).padStart(2, "0")}</span>
                    {stage.capabilities.length ? <ArrowRight className="h-4 w-4 text-ink-subtle" aria-hidden="true" /> : null}
                  </span>
                  <strong className="mt-4 block font-heading text-[19px] font-bold leading-snug text-ink"><BrandCopy>{stage.title}</BrandCopy></strong>
                  {stage.description ? <span className="mt-2 block text-[13px] leading-[1.65] text-ink-2"><BrandCopy>{stage.description}</BrandCopy></span> : null}
                  {stage.capabilities.length ? (
                    <span className="mt-5 flex flex-wrap gap-x-4 gap-y-2">
                      {stage.capabilities.map((capability, capabilityIndex) => (
                        <Link
                          key={capability.key}
                          to={`/modules/${capability.key}`}
                          className="group inline-flex min-h-11 items-center gap-2 rounded-lg text-[12px] font-semibold text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                          data-testid={capabilityIndex === 0 ? `buyer-stage-${capability.key}` : undefined}
                        >
                          {capability.name} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                        </Link>
                      ))}
                    </span>
                  ) : null}
                </>
              );
              const itemClassName = "min-h-[190px] border-b border-line md:border-r md:[&:nth-child(2n)]:border-r-0";
              return (
                <li key={`${stage.title}-${index}`} className={itemClassName}>
                  <div className="h-full min-h-[190px] p-6 text-left transition-colors duration-150 hover:bg-muted/60">{content}</div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}

function CapabilityLinks({ guide }) {
  const primary = validModules(guide.primaryCapabilityKeys);
  const primaryKeys = new Set(primary.map(({ key }) => key));
  const related = validModules(guide.relatedCapabilityKeys).filter(({ key }) => !primaryKeys.has(key));
  const all = [
    ...primary.map((mod) => ({ mod, relationship: "Core to this guide" })),
    ...related.map((mod) => ({ mod, relationship: "Related catalogue route" })),
  ];

  if (!all.length) return null;
  return (
    <section className="bg-canvas" aria-labelledby="buyer-capabilities-heading" data-testid="buyer-guide-capabilities">
      <div className="mx-auto max-w-7xl px-5 py-14 sm:py-16">
        <div className="grid gap-8 lg:grid-cols-[minmax(220px,0.52fr)_minmax(0,1.48fr)] lg:gap-14">
          <div>
            <p className="text-[12px] font-bold uppercase tracking-[0.13em] text-accent">Reviewed product routes</p>
            <h2 id="buyer-capabilities-heading" className="mt-2 font-heading text-[27px] font-bold leading-tight tracking-tight text-ink sm:text-[33px]">
              See each capability in detail.
            </h2>
            <p className="mt-4 text-[13.5px] leading-[1.72] text-ink-2">
              Each link opens the full page for that capability, with the setup, plan and provider detail behind it.
            </p>
          </div>

          <ul className="border-t border-line">
            {all.map(({ mod, relationship }) => {
              const Icon = mod.Icon || Layers3;
              return (
                <li key={mod.key} className="border-b border-line">
                  <Link
                    to={`/modules/${mod.key}`}
                    className="group grid min-h-[96px] grid-cols-[44px_minmax(0,1fr)_24px] items-center gap-4 rounded-lg py-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 sm:grid-cols-[44px_minmax(150px,0.5fr)_minmax(0,1fr)_24px]"
                    data-testid={`buyer-capability-${mod.key}`}
                  >
                    <span className="grid h-11 w-11 place-items-center rounded-xl border border-line bg-surface text-accent" aria-hidden="true"><Icon className="h-5 w-5" /></span>
                    <span>
                      <strong className="block text-[14px] font-semibold leading-snug text-ink">{mod.name}</strong>
                      <span className="mt-1 block text-[12px] font-bold uppercase tracking-[0.1em] text-ink-subtle">{relationship}</span>
                    </span>
                    <span className="col-span-3 row-start-2 text-[12.5px] leading-relaxed text-ink-2 sm:col-span-1 sm:col-start-3 sm:row-start-1"><BrandCopy>{mod.oneLiner}</BrandCopy></span>
                    <ArrowRight className="col-start-3 row-start-1 h-4 w-4 text-ink-subtle transition-transform group-hover:translate-x-0.5 sm:col-start-4" aria-hidden="true" />
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}

function BuyerGuideFaqs({ faqs = [] }) {
  if (!faqs.length) return null;
  return (
    <section className="border-y border-line bg-surface" aria-labelledby="buyer-guide-faq-heading" data-testid="buyer-guide-faqs">
      <div className="mx-auto grid max-w-7xl gap-8 px-5 py-14 sm:py-16 lg:grid-cols-[minmax(230px,0.55fr)_minmax(0,1.45fr)] lg:gap-14">
        <div>
          <p className="text-[12px] font-bold uppercase tracking-[0.13em] text-accent">Evaluation questions</p>
          <h2 id="buyer-guide-faq-heading" className="mt-2 font-heading text-[27px] font-bold leading-tight tracking-tight text-ink sm:text-[33px]">Know the boundary before rollout.</h2>
          <p className="mt-4 max-w-sm text-[13.5px] leading-[1.72] text-ink-2">Plan, provider, role and data dependencies are confirmed for your school during the review.</p>
        </div>
        <div className="border-t border-line">
          {faqs.map((faq, index) => (
            <details key={`${faq.q}-${index}`} className="group border-b border-line py-1">
              <summary className="grid min-h-14 cursor-pointer list-none grid-cols-[minmax(0,1fr)_32px] items-center gap-4 rounded-lg py-3 text-[14px] font-semibold text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 [&::-webkit-details-marker]:hidden">
                <span><BrandCopy>{faq.q}</BrandCopy></span>
                <span className="grid h-8 w-8 place-items-center rounded-full border border-line text-accent transition-transform duration-150 group-open:rotate-45" aria-hidden="true">+</span>
              </summary>
              <p className="max-w-3xl pb-5 pr-10 text-[13.5px] leading-[1.75] text-ink-2"><BrandCopy>{faq.a}</BrandCopy></p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function ModuleBuyerGuidePageContent() {
  const { guideKey, collectionKey, key } = useParams();
  const resolvedKey = guideKey || collectionKey || key;
  const guide = moduleBuyerGuideFor(resolvedKey);
  const navigate = useNavigate();
  const [salesOpen, setSalesOpen] = useState(false);

  useEffect(() => { initAnalytics(); }, []);
  useEffect(() => {
    if (!guide) return;
    window.scrollTo(0, 0);
    track("buyer_guide_viewed", { guide: guide.key, guide_type: guide.type });
  }, [guide]);

  const modules = useMemo(
    () => guide ? validModules([...(guide.primaryCapabilityKeys || []), ...(guide.relatedCapabilityKeys || [])]) : [],
    [guide],
  );
  // Normalise once, then use this same visible array for both disclosures and FAQPage schema.
  const guideFaqs = useMemo(() => buyerGuideFaqsFor(guide?.faqs), [guide]);
  const seo = useMemo(() => {
    if (!guide) return null;
    const path = guide.path || `/modules/guides/${guide.key}`;
    const description = guide.seoDescription || guide.seo?.description || guide.summary;
    const seoName = guide.shortTitle || guide.title;
    const fullTitle = `${seoName} Guide | Vidyapeeth360`;
    const title = fullTitle.length <= 60 ? fullTitle : `${seoName} | Vidyapeeth360`;
    return {
      title,
      description,
      path,
      schema: {
        "@context": "https://schema.org",
        "@graph": [
          { "@type": "CollectionPage", name: guide.title, description, url: `${seoOrigin}${path}` },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Modules", item: `${seoOrigin}/modules` },
              { "@type": "ListItem", position: 2, name: guide.title, item: `${seoOrigin}${path}` },
            ],
          },
          {
            "@type": "ItemList",
            name: guide.type === "capability-guide"
              ? `${guide.title} related catalogue routes`
              : `${guide.title} capabilities`,
            itemListElement: modules.map((mod, index) => ({
              "@type": "ListItem",
              position: index + 1,
              name: mod.name,
              url: `${seoOrigin}/modules/${mod.key}`,
            })),
          },
          faqPageSchemaFor(guideFaqs),
        ].filter(Boolean),
      },
    };
  }, [guide, guideFaqs, modules]);

  if (!guide) return <Navigate to="/modules" replace />;

  const openDemo = (placement) => {
    track("demo_request", { placement, guide: guide.key, context: guide.title });
    setSalesOpen(true);
  };

  return (
    <div
      className="landing-page flex min-h-screen flex-col bg-canvas text-ink"
      data-testid="module-buyer-guide-page"
      data-guide-key={guide.key}
      data-guide-type={guide.type}
    >
      <MarketingSeo {...seo} />
      {/* MarketingTopBar records its own placement before handing control to this page. */}
      <ScrollProgressBar />
      <MarketingTopBar onBookDemo={() => setSalesOpen(true)} />

      <main className="flex-1">
        <section
          className="mx-auto max-w-7xl px-5 pb-12 pt-10 sm:pb-16 sm:pt-14"
          aria-labelledby="buyer-guide-title"
          data-aira-fab-clearance="hide-mobile"
        >
          <div className="flex flex-wrap items-center gap-2.5">
            <Link
              to="/modules"
              className="inline-flex min-h-11 items-center gap-1.5 rounded-full border border-line bg-surface px-3.5 py-2 text-[12.5px] font-semibold text-ink-2 transition-colors hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
              data-testid="buyer-guide-back"
            >
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> All capabilities
            </Link>
            <span className="inline-flex min-h-9 items-center gap-1.5 rounded-full border border-line bg-surface px-3 text-[11.5px] font-bold uppercase tracking-[0.1em] text-accent">
              <Layers3 className="h-3.5 w-3.5" aria-hidden="true" /> {guideKind(guide)}
            </span>
            <span className="inline-flex min-h-9 items-center rounded-full bg-muted px-3 text-[11.5px] font-semibold text-ink-2">{guideStatus(guide)}</span>
          </div>

          <div className="mt-7 grid gap-9 lg:grid-cols-[minmax(0,1.08fr)_minmax(330px,0.72fr)] lg:items-end lg:gap-16">
            <div>
              <p className="text-[12px] font-bold uppercase tracking-[0.14em] text-accent"><BrandCopy>{guide.eyebrow || "Explore by school job"}</BrandCopy></p>
              <h1 id="buyer-guide-title" className="mt-3 max-w-4xl font-heading text-[38px] font-bold leading-[1.06] tracking-[-0.035em] text-ink sm:text-[50px] lg:text-[58px]">
                <BrandCopy>{guide.title}</BrandCopy>
              </h1>
              <p className="mt-5 max-w-3xl text-[17px] leading-[1.75] text-ink-2 sm:text-[18px]"><BrandCopy>{guide.summary}</BrandCopy></p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Button size="lg" onClick={() => openDemo("buyer_guide_hero")} data-testid="buyer-guide-demo">
                  {CTA_SECONDARY_LABEL} <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate("/register")} data-testid="buyer-guide-register">
                  {CTA_PRIMARY_LABEL}
                </Button>
              </div>
            </div>

            <aside
              className="overflow-hidden rounded-[26px] border border-line bg-surface shadow-[0_26px_70px_-52px_rgb(var(--vp-fg-default)/0.45)]"
              aria-label={`${guideKind(guide)} decision brief`}
              data-testid="buyer-guide-decision-brief"
            >
              <div className="flex items-center gap-3 border-b border-line px-6 py-4">
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-accent/10 text-accent" aria-hidden="true"><Route className="h-5 w-5" /></span>
                <div>
                  <p className="text-[12px] font-bold uppercase tracking-[0.11em] text-ink-subtle">Decision brief</p>
                  <p className="mt-0.5 text-[13px] font-semibold text-ink">One school job, connected product routes</p>
                </div>
              </div>
              <dl>
                <div className="border-b border-line px-6 py-5">
                  <dt className="text-[12px] font-bold uppercase tracking-[0.12em] text-ink-subtle">The problem</dt>
                  <dd className="mt-2 text-[13.5px] leading-[1.7] text-ink-2"><BrandCopy>{guide.problem}</BrandCopy></dd>
                </div>
                <div className="px-6 py-5">
                  <dt className="text-[12px] font-bold uppercase tracking-[0.12em] text-accent">The result to evaluate</dt>
                  <dd className="mt-2 font-heading text-[17px] font-semibold leading-[1.5] text-ink"><BrandCopy>{guide.outcome}</BrandCopy></dd>
                </div>
              </dl>
            </aside>
          </div>

          <div className="mt-10 flex items-start gap-3 border-y border-line py-4 text-[12.5px] leading-relaxed text-ink-2" data-testid="buyer-guide-disclosure">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
            <p>
              <strong className="font-semibold text-ink">Scope note:</strong>{" "}
              This page is an evaluation guide, not a separate entitlement.{" "}
              <BrandCopy>{guide.disclosure || "It groups reviewed capabilities without implying that every hand-off is automatic."}</BrandCopy>
            </p>
          </div>
        </section>

        <BuyerGuideVisualStage guide={guide} />
        <BuyerPassage guide={guide} />

        <section className="bg-canvas" aria-labelledby="buyer-scope-heading" data-testid="buyer-guide-scope">
          <div className="mx-auto max-w-7xl px-5 py-14 sm:py-16">
            <div className="max-w-3xl">
              <p className="text-[12px] font-bold uppercase tracking-[0.13em] text-accent">Product and rollout boundary</p>
              <h2 id="buyer-scope-heading" className="mt-2 font-heading text-[28px] font-bold leading-tight tracking-tight text-ink sm:text-[35px]">What the school gets — and what it must confirm.</h2>
            </div>
            <div className="mt-8 grid border-y border-line lg:grid-cols-2">
              <article className="py-8 lg:pr-12">
                <p className="text-[12px] font-bold uppercase tracking-[0.12em] text-positive">Included</p>
                <ScopeList items={guide.whatIncluded || guide.included} tone="included" />
              </article>
              <article className="border-t border-line py-8 lg:border-l lg:border-t-0 lg:pl-12">
                <p className="text-[12px] font-bold uppercase tracking-[0.12em] text-ink-subtle">Not included, or set up separately</p>
                <ScopeList items={guide.conditions} tone="conditions" />
              </article>
            </div>

            {guide.users?.length ? (
              <div className="grid gap-5 border-b border-line py-8 sm:grid-cols-[minmax(180px,0.45fr)_minmax(0,1.55fr)] sm:items-start" data-testid="buyer-guide-users">
                <div className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.14em] text-ink-subtle">
                  <CircleUserRound className="h-4 w-4 text-accent" aria-hidden="true" /> Who typically uses it
                </div>
                <ul className="flex flex-wrap gap-2">
                  {guide.users.map((user, index) => (
                    <li key={`${titleOf(user)}-${index}`} className="inline-flex min-h-11 items-center rounded-full border border-line bg-surface px-3.5 py-2 text-[13px] font-semibold text-ink-2">
                      <BrandCopy>{titleOf(user)}</BrandCopy>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </section>

        <CapabilityLinks guide={guide} />
        <BuyerGuideFaqs faqs={guideFaqs} />

        <section className="mx-auto max-w-7xl px-5 py-14 sm:py-16" aria-labelledby="buyer-guide-close-heading">
          <div className="grid gap-7 rounded-[28px] border border-line bg-surface p-7 shadow-[0_24px_70px_-50px_rgb(var(--vp-fg-default)/0.42)] sm:p-10 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
            <div>
              <p className="text-[12px] font-bold uppercase tracking-[0.13em] text-accent">Review it in your school context</p>
              <h2 id="buyer-guide-close-heading" className="mt-2 max-w-3xl font-heading text-[27px] font-bold leading-tight tracking-tight text-ink sm:text-[34px]">Map {guide.title.toLowerCase()} to your current process.</h2>
              <p className="mt-3 max-w-2xl text-[14px] leading-[1.7] text-ink-2">Bring your current roles, records, providers and rollout questions. We will walk through the applicable product routes and the boundaries that matter.</p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Button size="lg" onClick={() => openDemo("buyer_guide_close")} data-testid="buyer-guide-close-demo">
                {CTA_SECONDARY_LABEL} <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Button>
              <Link to="/pricing" className="inline-flex min-h-11 items-center gap-2 rounded-lg px-2 text-[13.5px] font-semibold text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60">
                Commercial overview <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <ContactSalesModal
        open={salesOpen}
        onClose={() => setSalesOpen(false)}
        contextLabel={`${guide.title} guide`}
        placement="buyer_guide"
      />
    </div>
  );
}

export default function ModuleBuyerGuidePage() {
  return <MarketingMotion><ModuleBuyerGuidePageContent /></MarketingMotion>;
}
